import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import type { AuthenticatedUser } from '@tiles-erp/shared-types';
import { env } from '../config/env';
import { apiFetch, tokenStorage } from '../lib/api-client';
import { authApi, type Credentials } from './auth-store';

interface AuthContextValue {
  user: AuthenticatedUser | null;
  isAuthenticated: boolean;
  isInitializing: boolean;
  login: (credentials: Credentials) => Promise<void>;
  logout: () => Promise<void>;
  hasPermission: (permission: string) => boolean;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

/** Holds the authenticated principal, restores the session on load and exposes auth actions. */
export function AuthProvider({ children }: { children: ReactNode }): JSX.Element {
  const [user, setUser] = useState<AuthenticatedUser | null>(null);
  const [isInitializing, setIsInitializing] = useState<boolean>(() =>
    Boolean(tokenStorage.getAccess()),
  );

  useEffect(() => {
    let cancelled = false;
    if (tokenStorage.getAccess()) {
      authApi
        .me()
        .then((me) => {
          if (!cancelled) setUser(me);
        })
        .catch(() => {
          tokenStorage.clear();
        })
        .finally(() => {
          if (!cancelled) setIsInitializing(false);
        });
    }
    return () => {
      cancelled = true;
    };
  }, []);

  const login = useCallback(async (credentials: Credentials) => {
    await authApi.login(credentials);
    setUser(await authApi.me());
  }, []);

  const logout = useCallback(async () => {
    await authApi.logout();
    setUser(null);
  }, []);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    let closingTimer: ReturnType<typeof setTimeout> | undefined;
    let deadline: number | null = null;
    const expire = () => {
      if (cancelled) return;
      // Clear locally immediately; a slow/offline logout request must not delay the lock.
      const revoke = authApi.logout();
      tokenStorage.clear();
      setUser(null);
      void revoke;
    };
    const check = async () => {
      if (deadline !== null && Date.now() >= deadline) { expire(); return; }
      try {
        const status = await apiFetch<{ allowed: boolean; serverNow: string; closesAt: string | null }>('/auth/access-status');
        if (cancelled) return;
        if (!status.allowed) { expire(); return; }
        if (closingTimer) clearTimeout(closingTimer);
        deadline = status.closesAt ? Date.now() + Date.parse(status.closesAt) - Date.parse(status.serverNow) : null;
        if (deadline !== null) closingTimer = setTimeout(expire, Math.max(0, deadline - Date.now()));
      } catch { /* Keep the last known closing deadline during network interruptions. */ }
    };
    void check();
    const interval = setInterval(() => void check(), 30000);
    const foreground = () => { if (!document.hidden) void check(); };
    document.addEventListener('visibilitychange', foreground);
    window.addEventListener('focus', foreground);
    return () => { cancelled = true; clearInterval(interval); if (closingTimer) clearTimeout(closingTimer); document.removeEventListener('visibilitychange', foreground); window.removeEventListener('focus', foreground); };
  }, [user]);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      isAuthenticated: env.authBypass || user !== null,
      isInitializing,
      login,
      logout,
      hasPermission: (permission) =>
        user !== null &&
        (user.roles.includes('SUPER_ADMIN') || user.permissions.includes(permission)),
    }),
    [user, isInitializing, login, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider');
  return ctx;
}
